import { type MessageItem } from "@koala/sdk";
import { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import ExpandedMessage from "../expandedMessage";
import {
  StyledCroppedImage,
  StyledCroppedImageContainer,
  StyledMessagePreview,
  StyledPreviewText,
} from "./styles";
import { LOYALTY_MESSAGE_TYPES, MESSAGE_HERO_TYPES } from "@/constants/loyalty";
import { useSelector } from "@/redux";
import meActions from "@/redux/me/actions";
import { toggleBodyScroll } from "@/utils/global";
import { dateFormatter } from "@/utils/loyalty";

export const MessageList = () => {
  const [activeMessage, setActiveMessage] = useState<MessageItem | null>(null);
  const { messages } = useSelector((state) => state.app.me);
  const dispatch = useDispatch();

  useEffect(() => {
    // Only default load a message on desktop
    if (!window.matchMedia(`(max-width: 767px)`).matches) {
      const messageToActivate = messages?.[0];

      if (!messageToActivate) {
        return;
      }

      setActiveMessage(messageToActivate);

      if (!messageToActivate?.read_at) {
        dispatch(meActions.markMessageAsRead(messageToActivate.id));
      }
    }
  }, []);

  const openMessage = (message: MessageItem) => {
    setActiveMessage(message);

    // If mobile, lock body scroll on expanded message
    if (window.matchMedia(`(max-width: 767px)`).matches) {
      toggleBodyScroll(!message);
    }

    // Mark message as read
    if (!message.read_at) {
      dispatch(meActions.markMessageAsRead(message.id));
    }
  };

  return (
    <>
      {messages?.map((message: MessageItem) => (
        <Fragment key={message.id}>
          <StyledMessagePreview
            aria-label="Click to read message"
            key={message.id}
            onClick={() => openMessage(message)}
            activeMessage={activeMessage?.id === message.id}
          >
            {/* Cropped preview image only if hero image (not video) exists */}
            {message.hero_content?.url &&
              message.hero_content?.type === MESSAGE_HERO_TYPES.IMAGE && (
                <StyledCroppedImageContainer
                  // Only display the 'unread' dot on message if personal message
                  isUnread={
                    message?.type === LOYALTY_MESSAGE_TYPES.PERSONAL &&
                    !message.read_at
                  }
                >
                  <StyledCroppedImage
                    src={message.hero_content?.url}
                    // @ts-expect-error TODO SDK
                    alt={message.hero?.alt_text}
                  />
                </StyledCroppedImageContainer>
              )}

            {/* Message content */}
            <StyledPreviewText
              messageRead={
                Boolean(message.read_at) ||
                message.type === LOYALTY_MESSAGE_TYPES.BUSINESS
              }
            >
              <h3>{message.title}</h3>

              {/* Either display subtitle or truncated message body */}
              <p>{message.subtitle || message.body?.substring(0, 200)}</p>

              {message.launch_time && (
                <p>{dateFormatter(message.launch_time)}</p>
              )}
            </StyledPreviewText>
          </StyledMessagePreview>

          {/* Each message preview will have it's own expanded message sibling */}
          {activeMessage?.id === message.id && (
            <ExpandedMessage
              message={message}
              closeMessage={() => setActiveMessage(null)}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};
